// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : react components for auth system
// =============================================================================
// ---Third-party imports
import React, {useEffect, useState} from 'react'
import {Button, Step, Segment, Grid, Table, Icon, Menu, Card, Checkbox} from 'semantic-ui-react'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import {VIcon} from 'uix/graphics'
import log from 'uix/logging'
import {get, isNil, strip, size} from 'ldash'
import connect from 'uix/connect'
import {useActions, useSelector, useUserSetting} from 'uix/hooks'
import {applyProps} from 'uix/components/utils'
import {DebugFeature, XPopup, Dialog} from 'uix/components'
import {formatDateTime} from 'uix/utils'

// ---Local imports
import {beginWebauthn, loginWebauthn, queryWebauthn} from './actions'
import {useUserData} from './hooks'
import {checkAcl} from './acl'
import {XMenu} from '../components'

// ---Globals
let REGISTRATION_STEPS = [
  // step 1 - initialize registration options
  {
    label: 'Create Options',
    active: 'REG_BEGIN',
    completed: ['REG_OPTIONS_CREATED', 'REG_COMPLETE'],
    failure: ['ERROR_REG_BEGIN'],
  },
  // step 2 - create and verify credentials
  {
    label: 'Verify Registration',
    active: 'REG_OPTIONS_CREATED',
    completed: ['REG_COMPLETE'],
    failure: ['ERROR_REG_CREDENTIALS_CREATE', 'ERROR_REG_CREDENTIALS_VERIFY'],
  },
]

let LOGIN_STEPS = [
  // step 1 - initialize login options
  {
    label: 'Create Options',
    active: 'LOGIN_BEGIN',
    completed: ['LOGIN_OPTIONS_CREATED', 'LOGIN_COMPLETE'],
    failure: ['ERROR_LOGIN_BEGIN'],
  },
  // step 2 - create and verify credentials
  {
    label: 'Verify Credentials',
    active: 'REG_OPTIONS_CREATED',
    completed: ['LOGIN_COMPLETE'],
    failure: ['ERROR_LOGIN_CREDENTIALS_CREATE', 'ERROR_LOGIN_CREDENTIALS_VERIFY'],
  },
]

export const WebAuthNDiag = connect(
  'WebAuthN',
  {
    storeMap: ['meta.user', 'meta.webauthn'],
    actionMap: {beginWebauthn, loginWebauthn, queryWebauthn},
  },
  ({user, beginWebauthn, loginWebauthn, queryWebauthn, webauthn, callApi}) => {
    useEffect(() => {
      if (isNil(webauthn.available)) {
        queryWebauthn(user.name)
      }
    }, [])

    let [keys, setKeys] = useState([])
    useEffect(() => {
      callApi('webauthn/keys', (data, error) => setKeys(data.keys))
    }, [webauthn])

    return (
      <Grid columns={2}>
        <Grid.Column width={6}>
          <Segment>
            <Button size="huge" onClick={() => beginWebauthn(user)} content="Register" />
            <Button
              size="huge"
              onClick={() => beginWebauthn(user, {reset: true})}
              content="Reset"
            />
            <Step.Group ordered size="tiny">
              {REGISTRATION_STEPS.map((r, i) => (
                <Step
                  key={i}
                  completed={r.completed.includes(webauthn.state)}
                  content={r.label}
                  active={webauthn.state == r.active}
                />
              ))}
            </Step.Group>
          </Segment>
          <Segment>
            <Button
              disabled={!Boolean(webauthn.available)}
              size="huge"
              onClick={() => loginWebauthn(user.name)}
              content={`Login with ${user.name}`}
            />
            <Step.Group ordered size="tiny">
              {LOGIN_STEPS.map((r, i) => (
                <Step
                  key={i}
                  completed={r.completed.includes(webauthn.state)}
                  content={r.label}
                  active={webauthn.state == r.active}
                />
              ))}
            </Step.Group>
          </Segment>
          <Segment>
            <tt>{webauthn.state}</tt>
            {webauthn.error ? <tt> {get(webauthn.error, 'errorMsg')}</tt> : null}
          </Segment>
        </Grid.Column>
        <Grid.Column width={10}>
          <Table definition>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell />
                {keys.map((k, i) => (
                  <Table.HeaderCell key={k.cred_id}>{`Key ${i}`}</Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>

            <Table.Body>
              <Table.Row>
                <Table.Cell>ID</Table.Cell>
                {keys.map(k => (
                  <Table.Cell key={k.cred_id}>{k.cred_id.slice(0, 20)}</Table.Cell>
                ))}
              </Table.Row>
              <Table.Row>
                <Table.Cell>Date</Table.Cell>
                {keys.map(k => (
                  <Table.Cell key={k.cred_id}>{formatDateTime(k.created_dt)}</Table.Cell>
                ))}
              </Table.Row>
              <Table.Row>
                <Table.Cell>PubKey</Table.Cell>
                {keys.map(k => (
                  <Table.Cell key={k.cred_id}>{k.pub_key.slice(0, 20)}</Table.Cell>
                ))}
              </Table.Row>
              <Table.Row>
                <Table.Cell>RP-ID</Table.Cell>
                {keys.map(k => (
                  <Table.Cell key={k.cred_id}>{k.rp_id}</Table.Cell>
                ))}
              </Table.Row>
              <Table.Row>
                <Table.Cell>UKey</Table.Cell>
                {keys.map(k => (
                  <Table.Cell key={k.cred_id}>{k.ukey}</Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          </Table>
        </Grid.Column>
      </Grid>
    )
  }
)

export const UserInfo = ({style, puid, title, shortenInitials = false}) => {
  /* a user name with an icon
   */
  puid = parseInt(puid)
  let curr_user = useSelector(Sel.user)
  let runImpersonify = useActions('runImpersonify')

  let user = useUserData(puid)
  let content = <Icon style={style} name="user outline" color="grey" title={`puid: ${puid}`} />

  if (user) {
    // non admin lookups emit only {puid, fullname}
    let name = user.fullname || user.login || ''

    if (shortenInitials) {
      let sep = name.includes('.') ? '.' : ' '
      let initials = name
        .split(sep)
        .map(n => strip(n)[0])
        .filter(e => e)
      if (!title) {
        title = name
      }
      // no short name for server user type
      name = user.type != 10 ? initials.map(s => `${s}.`).join(' ') : ''
    }
    let buildIcon = type => {
      if (type == 10) {
        return <Icon name="server" color="grey" />
      } else {
        return <Icon name="user" color="grey" />
      }
    }

    // handle debug user impersonify
    let onClick = null
    let styles = {paddingLeft: '2px', ...style}
    if (user.login && curr_user.hasGroup('debug')) {
      onClick = () => runImpersonify(user)
      styles = {...styles, cursor: 'pointer'}
    }

    return (
      <span style={styles} title={title || `puid: ${puid}`} onClick={onClick}>
        {buildIcon(user.type)}
        <span>{name}</span>
      </span>
    )
  }
  return content
}

export const LogoutMenuItem = () => {
  let runLogout = useActions(Actions.runLogout)
  return <Menu.Item onClick={runLogout} title="Logout" icon="sign out" />
}

const ResetPartPersistence = ({}) => {
  let clearPartPersistence = useActions('clearPartPersistence')

  let request = useSelector(Sel.request)
  let classCfg = useSelector(Sel.classCfg)
  let parts = [...classCfg.parts, ...classCfg.tabs[request.tabname].parts]
  parts = parts.map((p, i) => {
    let n = p.name || p.type
    return (
      <div key={`${n}_${i}`}>
        <Button
          fluid
          size="tiny"
          basic
          compact
          icon="history"
          content={`Reset ${n}`}
          onClick={() => clearPartPersistence(n)}
        />
      </div>
    )
  })

  return <div>{parts}</div>
}

const GlobalUserSettings = () => {
  /* custom user settings
  using persistence via store.root.ui.byId['Global/*']
  */

  let clearLocalStorage = useActions(Actions.clearLocalStorage)
  let switchTheme = useActions(Actions.switchTheme)
  let theme = useSelector(root => root.meta.theme.name)
  let [colorizeDomains, setColorizeDomains] = useUserSetting('colorizeDomains')
  let [headerStyle, setHeaderStyle] = useUserSetting('headerStyle')
  let [navBarTarget, setNavBarTarget] = useUserSetting('navBarTarget')
  const NavBarButtons = () => (
    <div style={{paddingBottom: '0.5rem'}}>
      <Button.Group
        style={{paddingRight: '0.5rem'}}
        size="mini"
        label="position"
        labeled
        color="grey"
      >
        <Button
          active={navBarTarget === 'leftPortal'}
          onClick={() => setNavBarTarget('leftPortal')}
          icon="chevron left"
        />
        <Button
          active={navBarTarget === 'topPortal'}
          onClick={() => setNavBarTarget('topPortal')}
          icon="chevron up"
        />
        <Button
          active={navBarTarget === 'centerLeftPortal'}
          onClick={() => setNavBarTarget('centerLeftPortal')}
          icon="circle outline"
        />
        <Button
          active={navBarTarget === 'centerBottomPortal'}
          onClick={() => setNavBarTarget('centerBottomPortal')}
          icon="chevron down"
        />
        <Button
          active={navBarTarget === 'centerRightPortal'}
          onClick={() => setNavBarTarget('centerRightPortal')}
          icon="chevron right"
        />
      </Button.Group>
      <span style={{fontSize: 'var(--vl_bodySecondary_fontSize)'}}>Position der Navigation</span>
    </div>
  )

  return (
    <>
      <Checkbox
        style={{marginBottom: '0.5rem'}}
        toggle
        checked={colorizeDomains}
        label="Colorize Domains"
        onClick={() => setColorizeDomains(!colorizeDomains)}
      />
      <Checkbox
        style={{marginBottom: '0.5rem'}}
        toggle
        checked={headerStyle === 'compact'}
        label="Compact Header"
        onClick={() => setHeaderStyle(headerStyle === 'default' ? 'compact' : 'default')}
      />
      <Checkbox
        style={{marginBottom: '0.5rem'}}
        toggle
        checked={theme === 'dark'}
        label="Dark Mode"
        onClick={() => switchTheme(theme === 'dark' ? 'default' : 'dark')}
      />
      <AclFeature acl="g:admin">
        <NavBarButtons />
      </AclFeature>
      <Button
        color="grey"
        icon="history"
        size="tiny"
        fluid
        content="Reset all Local Settings"
        onClick={clearLocalStorage}
      />
      <ResetPartPersistence />
    </>
  )
}

export const ProfileMenuItem = () => {
  let [openPopup, setOpenPopup] = useState(false)
  let [settings, setSettings] = useState(false)
  let user = useSelector(Sel.user)
  let userData = useUserData(user.puid)
  let runLogout = useActions(Actions.runLogout)

  const settingHandle = () => setSettings(!settings)

  if (!userData) {
    return <Menu.Item icon="user circle outline" />
  }

  return (
    <>
      <XPopup
        on="click"
        basic
        fitted
        position="bottom right"
        onOpen={() => setOpenPopup(!openPopup)}
        onClose={() => setOpenPopup(!openPopup)}
        trigger={<Menu.Item title="User Menü" icon="user circle outline" active={openPopup} />}
      >
        <Card>
          <Card.Content>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                verticalAlign: 'middle',
                justifyContent: 'space-between',
              }}
            >
              <div style={{padding: '0 0.5rem'}}>
                {user.fullname || user.name}
                <Card.Meta>{userData.email}</Card.Meta>
              </div>
              <XMenu style={{margin: 0}} secondary>
                <XMenu.Item active={settings} icon="cog" onClick={settingHandle} />
              </XMenu>
            </div>
          </Card.Content>
          {settings && (
            <Card.Content>
              <GlobalUserSettings />
            </Card.Content>
          )}
          <Card.Content textAlign="center" extra>
            <Button size="small" animated="fade" onClick={runLogout}>
              <Button.Content hidden>
                <VIcon name="sign-out"></VIcon>
              </Button.Content>
              <Button.Content visible>Logout</Button.Content>
            </Button>
          </Card.Content>
        </Card>
      </XPopup>
    </>
  )
}

export const UserMenuItem = () => {
  let setContextHelpOn = useActions(Actions.setContextHelpOn)
  let contextHelpOn = useSelector(root => root.meta.contextHelp.on)

  return (
    <>
      <Menu.Item
        onClick={() => setContextHelpOn(!contextHelpOn)}
        icon="question circle outline"
        title="Hilfe"
        active={contextHelpOn}
      />

      <ProfileMenuItem />
    </>
  )
}

export const AclFeature = ({acl, fallback, children, ...props}) => {
  /* render children if acl check passes
   */
  let user = useSelector(Sel.user)
  let {name: dname} = useSelector(Sel.domain)

  if (!checkAcl(acl, user, dname)) {
    if (fallback) {
      return fallback
    }
    return null
  }
  // a AclFeature in a XMenu needs props pass through
  return size(props) ? applyProps(children, props) : children
}
